import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, CircleCheck, CircleX, Eye, EyeOff, Search, TriangleAlert } from 'lucide-react';
import { api, ApiError, type Session } from '../../lib/api';
import { useTeamSpaces } from '../../lib/spaces';
import { relativeTime } from '../../lib/time';
import { errorText } from '../../i18n';
import '../../i18n/importMiro';
import { Modal, Spinner } from '@vatman/ui';
import './importMiro.css';

interface MiroBoard {
  id: string;
  name: string;
  description?: string;
  modifiedAt?: string;
  owner?: { name: string };
  team?: { name: string };
}

interface Row {
  type: string;
  count: number;
  reason: string;
}

interface Job {
  id: string;
  status: 'running' | 'done' | 'failed';
  phase: 'board' | 'items' | 'connectors' | 'images' | 'saving' | 'done';
  done: number;
  total: number;
  percent: number;
  sourceName?: string;
  error?: string;
  report?: {
    boardId: string;
    title: string;
    imported: number;
    importedByType: Record<string, number>;
    skipped: Row[];
    warnings: Row[];
  };
}

/** The running job survives closing the dialog (and a reload) within the tab. */
const JOB_KEY = 'vatman.miroImportJob';

const storedJob = (): string | null => {
  try {
    return sessionStorage.getItem(JOB_KEY);
  } catch {
    return null;
  }
};

const storeJob = (id: string | null) => {
  try {
    if (id) sessionStorage.setItem(JOB_KEY, id);
    else sessionStorage.removeItem(JOB_KEY);
  } catch {
    // Not critical: the dialog just won't resume after a reload.
  }
};

const codeOf = (e: unknown) => (e instanceof ApiError ? e.code : 'internal');

export function ImportMiroDialog({
  open,
  onOpenChange,
  session,
  defaultTeamId,
  defaultSpaceId,
  connection,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  session: Session;
  defaultTeamId?: string;
  defaultSpaceId?: string | null;
  /** Back from signing in to Miro (oauth.rs): the connection that stands in for a token. */
  connection?: string | null;
}) {
  const { t } = useTranslation();
  const [jobId, setJobId] = useState<string | null>(storedJob);
  const [token, setToken] = useState('');
  const [boards, setBoards] = useState<MiroBoard[] | null>(null);
  const [connError, setConnError] = useState<string | null>(null);
  const auth = connection ? { connection } : { token: token.trim() };

  // Signed in to Miro: straight to the list of boards.
  useEffect(() => {
    if (!open || !connection) return;
    setConnError(null);
    api
      .post<MiroBoard[]>('/import/miro/boards', { connection })
      .then(setBoards)
      .catch((err) => setConnError(errorText(codeOf(err))));
  }, [open, connection]);

  const finished = useRef(false);

  const setJob = (id: string | null) => {
    storeJob(id);
    setJobId(id);
    finished.current = false;
  };

  // A finished import is shown until the dialog closes; the next time it starts fresh.
  const changeOpen = (v: boolean) => {
    if (!v && finished.current) setJob(null);
    onOpenChange(v);
  };

  return (
    <Modal open={open} onOpenChange={changeOpen} title={t('importMiro.title')} width={560}>
      <div className="mi">
        {jobId ? (
          <JobView
            jobId={jobId}
            onFinished={() => {
              storeJob(null);
              finished.current = true;
            }}
            onAgain={() => setJob(null)}
            onClose={() => changeOpen(false)}
          />
        ) : boards ? (
          <Picker
            auth={auth}
            boards={boards}
            session={session}
            defaultTeamId={defaultTeamId}
            defaultSpaceId={defaultSpaceId}
            onBack={() => setBoards(null)}
            onStarted={setJob}
          />
        ) : (
          <TokenStep token={token} setToken={setToken} onBoards={setBoards} oauth={!!session.oauth?.miro} error={connError} />
        )}
      </div>
    </Modal>
  );
}

function TokenStep({
  token,
  setToken,
  onBoards,
  oauth,
  error: outer,
}: {
  token: string;
  setToken: (v: string) => void;
  onBoards: (b: MiroBoard[]) => void;
  /** The installation has Miro's OAuth app: signing in replaces the token. */
  oauth: boolean;
  error: string | null;
}) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(outer);
  useEffect(() => setError(outer), [outer]);
  const signIn = async () => {
    setBusy(true);
    try {
      const r = await api.get<{ url: string }>('/import/miro/oauth/start');
      window.location.assign(r.url);
    } catch (err) {
      setError(errorText(codeOf(err)));
      setBusy(false);
    }
  };
  const [show, setShow] = useState(false);
  const steps = t('importMiro.steps', { returnObjects: true }) as string[];

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!token.trim()) return;
    setBusy(true);
    setError(null);
    try {
      onBoards(await api.post<MiroBoard[]>('/import/miro/boards', { token: token.trim() }));
    } catch (err) {
      setError(errorText(codeOf(err)));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="mi-stack">
      <p className="muted mi-lead">{t('importMiro.subtitle')}</p>
      {oauth && (
        <>
          <button type="button" className="btn primary lg" disabled={busy} onClick={() => void signIn()}>
            {t('importMiro.signIn')}
          </button>
          <div className="mi-or muted">{t('importMiro.orToken')}</div>
        </>
      )}
      <div className="mi-howto">
        <div className="mi-howto-title">{t('importMiro.howTo')}</div>
        <ol>
          {steps.map((s, i) => (
            <li key={i}>{s}</li>
          ))}
        </ol>
      </div>
      <label className="field">
        <span>{t('importMiro.tokenLabel')}</span>
        <div className="mi-token">
          <input
            className="input"
            type={show ? 'text' : 'password'}
            value={token}
            onChange={(e) => setToken(e.target.value)}
            autoComplete="off"
            spellCheck={false}
            autoFocus
            name="miro-token"
          />
          <button type="button" className="icon-btn sm" onClick={() => setShow((v) => !v)} aria-label={t(show ? 'importMiro.hideToken' : 'importMiro.showToken')}>
            {show ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        </div>
      </label>
      <p className="subtle mi-note">{t('importMiro.privacy')}</p>
      {error && <div className="error-text">{error}</div>}
      <div className="mi-actions">
        <button className="btn primary" disabled={busy || !token.trim()}>
          {busy && <Spinner />} {t('importMiro.connect')}
        </button>
      </div>
    </form>
  );
}

function teamLabel(session: Session, teamId: string, personal: string): string {
  const team = session.teams.find((tm) => tm.id === teamId);
  if (!team) return '';
  if (team.personal) return personal;
  const org = session.orgs.find((o) => o.id === team.orgId);
  return org ? `${org.name} · ${team.name}` : team.name;
}

function Picker({
  auth,
  boards,
  session,
  defaultTeamId,
  defaultSpaceId,
  onBack,
  onStarted,
}: {
  /** A pasted token, or a sign-in to Miro. */
  auth: { token: string } | { connection: string };
  boards: MiroBoard[];
  session: Session;
  defaultTeamId?: string;
  defaultSpaceId?: string | null;
  onBack: () => void;
  onStarted: (jobId: string) => void;
}) {
  const { t } = useTranslation();
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState<string | null>(boards.length === 1 ? boards[0].id : null);
  const [teamId, setTeamId] = useState(defaultTeamId ?? session.teams[0]?.id ?? '');
  const [spaceId, setSpaceId] = useState<string>(defaultSpaceId ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const spaces = useTeamSpaces(teamId);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return s ? boards.filter((b) => b.name.toLowerCase().includes(s)) : boards;
  }, [boards, q]);

  const start = async () => {
    if (!selected) return;
    setBusy(true);
    setError(null);
    try {
      const res = await api.post<{ jobId: string }>('/import/miro', {
        ...auth,
        miroBoardId: selected,
        teamId: teamId || undefined,
        spaceId: spaceId || undefined,
      });
      onStarted(res.jobId);
    } catch (err) {
      setError(errorText(codeOf(err)));
      setBusy(false);
    }
  };

  return (
    <div className="mi-stack">
      <div className="field">
        <span>{t('importMiro.chooseBoard')}</span>
        {boards.length > 6 && (
          <div className="search-field">
            <Search size={15} />
            <input className="input" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('importMiro.searchBoards')} autoFocus />
          </div>
        )}
        <div className="mi-boards" role="radiogroup">
          {boards.length === 0 && <div className="empty-state-panel">{t('importMiro.noBoards')}</div>}
          {boards.length > 0 && filtered.length === 0 && <div className="empty-state-panel">{t('importMiro.nothingFound')}</div>}
          {filtered.map((b) => (
            <button key={b.id} type="button" role="radio" aria-checked={selected === b.id} className="mi-board" data-selected={selected === b.id} onClick={() => setSelected(b.id)} onDoubleClick={start}>
              <span className="mi-radio" />
              <span className="mi-board-text">
                <span className="mi-board-name">{b.name || b.id}</span>
                <span className="mi-board-meta">
                  {[b.team?.name, b.owner?.name, b.modifiedAt ? t('importMiro.edited', { time: relativeTime(Date.parse(b.modifiedAt)) }) : null].filter(Boolean).join(' · ')}
                </span>
              </span>
            </button>
          ))}
        </div>
      </div>
      <div className="field">
        <span>{t('importMiro.targetTeam')}</span>
        <div className="mi-target">
          <select
            className="input"
            value={teamId}
            onChange={(e) => {
              setTeamId(e.target.value);
              setSpaceId('');
            }}
          >
            {session.teams.map((tm) => (
              <option key={tm.id} value={tm.id}>
                {teamLabel(session, tm.id, t('nav.personal'))}
              </option>
            ))}
          </select>
          {(spaces.data?.length ?? 0) > 1 && (
            <select className="input" value={spaceId || (spaces.data?.find((s) => s.isDefault)?.id ?? '')} onChange={(e) => setSpaceId(e.target.value)}>
              {spaces.data?.map((sp) => (
                <option key={sp.id} value={sp.id}>
                  {sp.icon ? `${sp.icon} ` : ''}
                  {sp.name}
                </option>
              ))}
            </select>
          )}
        </div>
      </div>
      {error && <div className="error-text">{error}</div>}
      <div className="mi-actions spread">
        <button type="button" className="btn ghost" onClick={onBack}>
          <ArrowLeft size={16} /> {t('importMiro.changeToken')}
        </button>
        <button className="btn primary" disabled={!selected || busy} onClick={start}>
          {busy && <Spinner />} {t('importMiro.start')}
        </button>
      </div>
    </div>
  );
}

function JobView({ jobId, onFinished, onAgain, onClose }: { jobId: string; onFinished: () => void; onAgain: () => void; onClose: () => void }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const job = useQuery({
    queryKey: ['miroImport', jobId],
    queryFn: () => api.get<Job>(`/import/jobs/${jobId}`),
    refetchInterval: (q) => (q.state.data && q.state.data.status !== 'running' ? false : 500),
    retry: (n, e) => !(e instanceof ApiError && e.status === 404) && n < 3,
  });
  const status = job.data?.status;

  const finishedRef = useRef(onFinished);
  finishedRef.current = onFinished;
  const over = status === 'done' || status === 'failed' || !!job.error;
  useEffect(() => {
    if (status === 'done') void qc.invalidateQueries({ queryKey: ['boards'] });
  }, [status, qc]);
  useEffect(() => {
    if (over) finishedRef.current();
  }, [over]);

  // The job is gone (server restarted or it expired): start over.
  if (job.error) {
    return (
      <div className="mi-stack mi-center">
        <CircleX size={40} className="mi-icon failed" />
        <div className="error-text">{errorText(codeOf(job.error))}</div>
        <div className="mi-actions">
          <button className="btn primary" onClick={onAgain}>
            {t('importMiro.retry')}
          </button>
        </div>
      </div>
    );
  }
  const j = job.data;
  if (!j) {
    return (
      <div className="mi-center">
        <Spinner />
      </div>
    );
  }

  if (j.status === 'failed') {
    return (
      <div className="mi-stack mi-center">
        <CircleX size={40} className="mi-icon failed" />
        <h3 className="mi-h">{t('importMiro.failedTitle')}</h3>
        <p className="muted">{errorText(j.error ?? 'internal')}</p>
        <div className="mi-actions">
          <button className="btn primary" onClick={onAgain}>
            {t('importMiro.retry')}
          </button>
        </div>
      </div>
    );
  }

  if (j.status === 'running' || !j.report) {
    return (
      <div className="mi-stack">
        {j.sourceName && <div className="mi-source">{j.sourceName}</div>}
        <div className="mi-progress" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={j.percent}>
          <div className="mi-progress-bar" style={{ width: `${Math.max(2, j.percent)}%` }} />
        </div>
        <div className="mi-phase">
          <span>{t(`importMiro.phases.${j.phase}`, { done: j.done, total: j.total })}</span>
          <span className="mi-percent">{j.percent}%</span>
        </div>
        <p className="subtle mi-note">{t('importMiro.background')}</p>
      </div>
    );
  }

  const r = j.report;
  const typeName = (type: string) => t(`importMiro.types.${type}`, { defaultValue: type });
  const reason = (code: string) => t(`importMiro.reasons.${code}`, { defaultValue: code });
  return (
    <div className="mi-stack">
      <div className="mi-done">
        <CircleCheck size={40} className="mi-icon ok" />
        <div>
          <h3 className="mi-h">{t('importMiro.doneTitle', { title: r.title })}</h3>
          <div className="muted">{t('importMiro.imported', { count: r.imported })}</div>
        </div>
      </div>
      {Object.keys(r.importedByType).length > 0 && (
        <div className="mi-chips">
          {Object.entries(r.importedByType).map(([type, n]) => (
            <span key={type} className="chip">
              {typeName(type)} <b>{n}</b>
            </span>
          ))}
        </div>
      )}
      <ReportRows title={t('importMiro.skippedTitle')} rows={r.skipped} typeName={typeName} reason={reason} kind="skipped" />
      <ReportRows title={t('importMiro.warningsTitle')} rows={r.warnings} typeName={typeName} reason={reason} kind="warning" />
      <div className="mi-actions spread">
        <button className="btn ghost" onClick={onAgain}>
          {t('importMiro.another')}
        </button>
        <button
          className="btn primary"
          onClick={() => {
            onClose();
            navigate(`/b/${r.boardId}`);
          }}
        >
          {t('importMiro.openBoard')}
        </button>
      </div>
    </div>
  );
}

function ReportRows({
  title,
  rows,
  typeName,
  reason,
  kind,
}: {
  title: string;
  rows: Row[];
  typeName: (t: string) => string;
  reason: (c: string) => string;
  kind: 'skipped' | 'warning';
}) {
  if (!rows.length) return null;
  return (
    <div className="mi-report" data-kind={kind}>
      <div className="mi-report-title">
        {kind === 'skipped' ? <CircleX size={15} /> : <TriangleAlert size={15} />} {title}
      </div>
      <ul>
        {rows.map((r) => (
          <li key={`${r.type}:${r.reason}`}>
            <span>{typeName(r.type)}</span>
            <span className="mi-count">{r.count}</span>
            <span className="muted">{reason(r.reason)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
